import { withSelectedCompany } from '@/modules/shared/hocs/withSelectedCompany'
import { describeTaskProgress } from '@/modules/shared/utils/taskProgressLabel'
import { useElapsedTick } from '@/modules/shared/hooks/useElapsedTick'
import { useShallow } from 'zustand/shallow'
import { useRouteCompanyId } from '@/modules/shared/hooks/useRouteCompanyId'
import * as React from 'react'

import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

import { MessageModel } from '@/app/models/messageModel'

import { MessageType } from '@/app/constants/messageType'
import { TaskModel } from '@/app/models/tasks/taskModel'
import { TaskStatus as TaskStatusEnum } from '@/app/constants/taskStatus'
import { TaskType } from '@/app/constants/taskType'
import { getReportNameByType } from '@/app/services/printout/reportTypeResolver'
import {
  isEmptyDocumentCode,
  resolveEmptyDocumentDetails,
} from '@/app/services/printout/emptyDocumentReason'
import { addTask } from '@/app/actions/task'
import { showMessage } from '@/modules/shared/stores/messageStore'

import { toast } from '@/lib/toasts/api'
import { fetchBlobForPreview } from '@/modules/printouts/services/BaseService'
import { TaskStatusBanner } from './TaskStatusBanner'
import { usePrintPreview } from '@/modules/printouts/stores/printPreviewStore'
import {
  selectCompletedTasks,
  selectOwnRunningTasksOf,
  useTaskTrackerStore,
} from '@/modules/shared/stores/taskTrackerStore'
import { useBatchPrintoutStore } from '@/modules/printouts/stores/batchPrintoutStore'
import {
  extractBatchFiles,
  resolveFolder,
} from '@/modules/printouts/utils/batchFiles'
import {
  zipPreviewItems,
  downloadBlob,
  downloadPreviewItem,
} from '@/modules/printouts/utils/downloadPreviewItems'
import { withRouter, RouteComponentProps } from '@/app/navigation/withRouter'
import { getBusinessTripTaskDetails } from '@/modules/shared/utils/businessTripTaskDetails'
import { recordBusinessTripFailures } from '@/modules/shared/utils/recordBusinessTripFailures'
import { invalidateAfterImport } from '@/modules/imports/utils/invalidateAfterImport'
import { useDriverActivityStore } from '@/modules/drivers/stores/activityStore'
import { useImiExportErrorsStore } from '@/modules/exports/stores/imiExportErrorsStore'
import { useQueryClient } from '@tanstack/react-query'
import useBusinessTripService from '@/modules/drivers/services/BusinessTripService'

type BatchBusinessTripResult = {
  DriverId?: number
  driverId?: number
  Success?: boolean
  success?: boolean
  Skipped?: boolean
  skipped?: boolean
  Code?: string
  code?: string
  Message?: string
  message?: string
  RequiresCoDriverDecision?: boolean
  requiresCoDriverDecision?: boolean
  Events?: any[]
  events?: any[]
}

type CoDriverCopyOption = {
  key: string
  driverId: number
  optionIndex: number
  tripDates: string
  coDriverName: string
}

type CoDriverDecisionDriver = {
  driverId: number
  events: any[]
  options: CoDriverCopyOption[]
}

type CoDriverDecisionState = {
  task: TaskModel
  drivers: CoDriverDecisionDriver[]
  checked: Record<string, boolean>
  saving: boolean
}

type TaskStatusStateProps = {
  companyId: string
  checkedDrivers: Array<string>
}

type TaskStatusDispatchProps = {
  showMessage: (message: MessageModel) => void
  addTask: (id: string, type: TaskType, data: any) => void
}

type TaskStatusProps = TaskStatusStateProps &
  TaskStatusDispatchProps &
  RouteComponentProps<any>

const isCoDriverDecisionResult = (result: BatchBusinessTripResult) =>
  Boolean(
    result.RequiresCoDriverDecision ??
      result.requiresCoDriverDecision ??
      (result.Code ?? result.code) === 'COPY_TO_CODRIVER',
  )

const getCoDriverDecisionState = (
  task: TaskModel,
): CoDriverDecisionState | null => {
  if (!task.logs) {
    return null
  }

  try {
    const results = JSON.parse(task.logs) as BatchBusinessTripResult[]
    if (!Array.isArray(results)) {
      return null
    }

    const checked: Record<string, boolean> = {}
    const drivers = results
      .filter(isCoDriverDecisionResult)
      .map((result) => {
        const driverId = result.DriverId ?? result.driverId
        const events = result.Events ?? result.events ?? []
        const message = result.Message ?? result.message ?? ''
        if (!driverId || !Array.isArray(events) || events.length === 0) {
          return null
        }

        let optionIndex = 0
        const options = message.split('|').flatMap((tripSegment) => {
          const separatorIndex = tripSegment.indexOf(';')
          if (separatorIndex < 0) {
            return []
          }

          const tripDates = tripSegment.substring(0, separatorIndex).trim()
          return tripSegment
            .substring(separatorIndex + 1)
            .split(',')
            .map((driver) => driver.trim())
            .filter(Boolean)
            .map((coDriverName) => {
              const key = `${driverId}:${optionIndex}`
              checked[key] = true
              return {
                key,
                driverId,
                optionIndex: optionIndex++,
                tripDates,
                coDriverName,
              }
            })
        })

        if (options.length === 0) {
          return null
        }

        return {
          driverId,
          events,
          options,
        }
      })
      .filter(Boolean) as CoDriverDecisionDriver[]

    if (drivers.length === 0) {
      return null
    }

    return {
      task,
      drivers,
      checked,
      saving: false,
    }
  } catch {
    return null
  }
}

const downloadArchive = (blobUrl: string, filename: string) => {
  downloadPreviewItem({ blobUrl, filename })
  window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000)
}

const TaskStatusComponent: React.FC<TaskStatusProps> = ({
  companyId,
  showMessage,
  addTask,
  location,
  history,
}) => {
  const { translate } = useLocalize()
  const routeCompanyId = useRouteCompanyId()

  const tasks = useTaskTrackerStore(
    useShallow(selectOwnRunningTasksOf(routeCompanyId || companyId)),
  ) as Array<TaskModel>
  const completedTasks = useTaskTrackerStore(
    useShallow(selectCompletedTasks),
  ) as Array<TaskModel>

  const taskVisited = useTaskTrackerStore((s) => s.consumeCompleted)

  const { showPreview, showPreviewGroup } = usePrintPreview()
  const { saveGroupedBatchTask } = useBusinessTripService()
  const queryClient = useQueryClient()
  const [coDriverDecision, setCoDriverDecision] =
    React.useState<CoDriverDecisionState | null>(null)
  const t = (key: string, ...args: any[]) => {
    return translate(key, ...args).toString()
  }


  const flushFinishedBatch = () => {
    const docs = useBatchPrintoutStore.getState().takeFinishedBatch()
    if (!docs) return

    const files = docs
      .filter((doc) => doc.status === 'ready')
      .flatMap((doc) => doc.files)

    const pdfs = files.filter((file) =>
      file.filename.toLowerCase().endsWith('.pdf'),
    )


    const others = files.filter(
      (file) => !file.filename.toLowerCase().endsWith('.pdf'),
    )

    if (others.length === 1) {
      downloadPreviewItem(others[0])
    } else if (others.length > 1) {
      zipPreviewItems(others)
        .then((archive) => {
          downloadBlob(
            new Blob([archive], { type: 'application/zip' }),
            t('printout.batch-zip-name'),
          )
          others.forEach((file) => URL.revokeObjectURL(file.blobUrl))
        })
        .catch((error) => {
          console.error('Nie udalo sie spakowac dokumentow:', error)
          others.forEach(downloadPreviewItem)
        })
    }

    if (pdfs.length > 0) {
      showPreviewGroup(pdfs)
    }

    const failed = docs.filter((doc) => doc.status === 'error')
    if (failed.length > 0) {
      showMessage({
        text: t('printout.processed-error'),
        details: failed
          .map(
            (doc) =>
              `${doc.reportType ? t(getReportNameByType(doc.reportType)) : ''}${
                doc.errorDetails ? ` — ${doc.errorDetails}` : ''
              }`,
          )
          .join('\n'),
        type: MessageType.WARNING,
        freeze: true,
      } as MessageModel)
    }
  }


  const batchDocs = useBatchPrintoutStore((s) => s.docs)
  const batchSealed = useBatchPrintoutStore((s) => s.sealed)

  React.useEffect(() => {
    flushFinishedBatch()
  }, [batchDocs, batchSealed])

  const intervalRef = React.useRef<any | null>(null)
  const prevCompletedTasksRef = React.useRef<Array<TaskModel>>([])
  const shownToastIdsRef = React.useRef<Set<string>>(new Set())
  const activeToastIdRef = React.useRef<string | number | null>(null)

  const handleCoDriverDecisionSave = React.useCallback(
    async (copyToCoDriver: boolean) => {
      if (!coDriverDecision || coDriverDecision.saving) {
        return
      }

      setCoDriverDecision({
        ...coDriverDecision,
        saving: true,
      })

      try {
        // One request for the whole selection. This used to be a
        // Promise.all over saveGrouped - a POST per driver, all at once, which
        // for a company-wide generation meant hundreds of parallel heavy saves,
        // no progress, and one rejection hiding the rest. The server loops the
        // drivers and reports through a task, exactly like the generation this
        // dialog is finishing.
        const drivers = coDriverDecision.drivers.map((driver) => ({
          driverId: driver.driverId,
          events: driver.events,
          checkedTripCoDrivers: copyToCoDriver
            ? driver.options.map(
                (option) => coDriverDecision.checked[option.key] ?? true,
              )
            : [],
          defaultStartDate:
            driver.events[0]?.date ?? driver.events[0]?.Date ?? undefined,
        }))

        const result = await saveGroupedBatchTask({
          companyId,
          drivers,
          defaultStartDate:
            coDriverDecision.task.data?.defaultStartDate ??
            drivers[0]?.defaultStartDate,
          copyToCoDriver,
        })

        if (!result?.taskId) {
          throw new Error(t('businessTrip.generate-failed'))
        }

        // Registered like any other batch: the polling loop below picks it up and
        // the completion branch reports the outcome, so the dialog can close now.
        addTask(result.taskId, TaskType.BusinessTrip, {
          defaultStartDate: coDriverDecision.task.data?.defaultStartDate,
          progressCurrent: 0,
          progressTotal: drivers.length,
        })

        setCoDriverDecision(null)
      } catch (error: any) {
        showMessage({
          text: t('businessTrip.generate-failed'),
          details: error?.message ?? '',
          type: MessageType.ERROR,
          freeze: true,
        } as MessageModel)
        setCoDriverDecision({
          ...coDriverDecision,
          saving: false,
        })
      }
    },
    [
      addTask,
      coDriverDecision,
      companyId,
      saveGroupedBatchTask,
      showMessage,
      t,
    ],
  )

  // Show toast for active tasks
  React.useEffect(() => {
    if (tasks.length > 0) {
      const isViolationCalculationInProgress = tasks.some(
        (t) => t.type === TaskType.Violation,
      )
      const isTimesheetInProgress = tasks.some(
        (t) => t.type === TaskType.Timesheet,
      )
      const isSaveImportInProgress = tasks.some(
        (t) => t.type === TaskType.SaveImport,
      )
      const imiExportInProgress = tasks.some(
        (t) => t.type === TaskType.ImiExport,
      )
      const businessTripGenerationInProgress = tasks.some(
        (t) => t.type === TaskType.BusinessTrip,
      )
    
      const businessTripCostsRefreshInProgress = tasks.some(
        (t) =>
          t.type === TaskType.BusinessTrip &&
          (t as any).data?.operation === 'refreshCosts',
      )
      const businessTripBulkOperationInProgress = tasks.some(
        (t) =>
          t.type === TaskType.BusinessTrip &&
          (t as any).data?.operation === 'tripBulkOperation',
      )

      const message = isTimesheetInProgress
        ? t('timesheet.calculation-in-progress')
        : isViolationCalculationInProgress
          ? t('driverViolations.calculation-in-progress')
          : isSaveImportInProgress
            ? t('importPanel.calculation-in-progress')
            : imiExportInProgress
              ? t('exportModule.submitting-in-progress')
              : businessTripBulkOperationInProgress
                ? t('businessTrip.bulk-operation-in-progress')
                : businessTripCostsRefreshInProgress
                  ? t('businessTrip.costs-refresh-in-progress')
                  : businessTripGenerationInProgress
                    ? t('businessTrip.generation-in-progress')
                    : t('printout.processing-in-progress')

      // Show toast only once for current batch of tasks
      const taskIds = tasks
        .map((t) => t.id)
        .sort()
        .join(',')
      if (!shownToastIdsRef.current.has(taskIds)) {
        // Dismiss previous toast if exists
        if (activeToastIdRef.current) {
          toast.dismiss(activeToastIdRef.current)
        }

        // TODO: re-enable when needed
        // activeToastIdRef.current = toast.info({
        //   text: message,
        //   autoCloseMs: 0, // Don't auto-close
        //   dismissible: false,
        //   showProgress: false,
        // })
        shownToastIdsRef.current.add(taskIds)
      }
    } else {
      // Clear toast when all tasks complete
      if (activeToastIdRef.current) {
        toast.dismiss(activeToastIdRef.current)
        activeToastIdRef.current = null
      }
      shownToastIdsRef.current.clear()
    }
  }, [tasks, t])

  // Handle completed tasks
  React.useEffect(() => {
    const prevCompleted = prevCompletedTasksRef.current
    const newCompleted = completedTasks.filter(
      (task) => !prevCompleted.some((prev) => prev.id === task.id),
    )

    newCompleted.forEach((completedTask) => {
      if (completedTask.type === TaskType.Timesheet) {
        showMessage({
          text:
            completedTask.status === TaskStatusEnum.Completed
              ? t('timesheet.calculated-title')
              : t('timesheet.calculated-error'),
          details:
            completedTask.status === TaskStatusEnum.Completed
              ? t('timesheet.calculated-details')
              : completedTask.message || '',
          type:
            completedTask.status === TaskStatusEnum.Completed
              ? MessageType.INFO
              : MessageType.ERROR,
          freeze: completedTask.status === TaskStatusEnum.Error,
        } as MessageModel)
      } else if (completedTask.type === TaskType.Export) {
        // Eksport CSV: plik zjezdza prosto na dysk, bez podgladu - to arkusz, nie
        // dokument do ogladania. Sciezka jest ta sama co przy wydrukach, bo eksport
        // przeniosl sie na te sama kolejke (patrz GenerateExportJob).
        if (completedTask.status === TaskStatusEnum.Completed) {
          fetchBlobForPreview(
            `/config-api/importTask/downloadOutputFile?id=${completedTask.id}`,
            {},
            'text/csv',
          )
            .then(({ blobUrl, filename }) => {
              // downloadPreviewItem przyjmuje gotowy adres blob i sam pilnuje
              // zwolnienia go po pobraniu.
              downloadPreviewItem({ blobUrl, filename })
              window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000)
            })
            .catch((error) => {
              console.error('Error downloading export:', error)
              showMessage({
                text: t('exportModule.export-error'),
                type: MessageType.ERROR,
                freeze: true,
              } as MessageModel)
            })
        } else {
          showMessage({
            text: t('exportModule.export-error'),
            details: completedTask.message ?? '',
            type: MessageType.ERROR,
            freeze: true,
          } as MessageModel)
        }
      } else if (completedTask.type === TaskType.Printout) {
        // Dokumenty z wydruku grupowego nie otwieraja podgladu pojedynczo -
        // trafiaja do wspolnego podsumowania po zakonczeniu calej paczki.
        const isBatchDocument = useBatchPrintoutStore
          .getState()
          .isTracked(completedTask.id)

        if (completedTask.status === TaskStatusEnum.Completed) {
          fetchBlobForPreview(
            `/config-api/importTask/downloadOutputFile?id=${completedTask.id}`,
            {},
          )
            .then(({ blobUrl, filename, mimeType }) => {
              const isZip =
                mimeType === 'application/zip' ||
                filename.toLowerCase().endsWith('.zip')

              const isPdf =
                !isZip &&
                (mimeType === 'application/pdf' ||
                  filename.toLowerCase().endsWith('.pdf'))

              if (isBatchDocument) {
                const resolveBatchDoc = async () => {
                  const subjects = useBatchPrintoutStore
                    .getState()
                    .getSubjects()
                  const files = isZip
                    ? await extractBatchFiles(blobUrl, subjects)
                    : [
                        {
                          blobUrl,
                          filename,
                          folder: resolveFolder(filename, subjects),
                        },
                      ]

                  if (isZip) URL.revokeObjectURL(blobUrl)

                  useBatchPrintoutStore
                    .getState()
                    .resolveTask(completedTask.id, {
                      status: 'ready',
                      files,
                      isPdf: isZip
                        ? files.every((f) =>
                            f.filename.toLowerCase().endsWith('.pdf'),
                          )
                        : isPdf,
                    })
                  flushFinishedBatch()
                }

                resolveBatchDoc().catch((error) => {
                  console.error('Nie udalo sie odczytac paczki wydruku:', error)
                  useBatchPrintoutStore
                    .getState()
                    .resolveTask(completedTask.id, {
                      status: 'error',
                      errorDetails: t('printout.processed-error'),
                    })
                  flushFinishedBatch()
                })
                return
              }

              if (isZip) {
                extractBatchFiles(blobUrl, [])
                  .then((files) => {
                    const pdfs = files.filter((file) =>
                      file.filename.toLowerCase().endsWith('.pdf'),
                    )

                    if (pdfs.length === 0) {
                      files.forEach((file) => URL.revokeObjectURL(file.blobUrl))
                      downloadArchive(blobUrl, filename)
                      return
                    }

                    const others = files.filter((file) => !pdfs.includes(file))
                    if (others.length > 0) {
                      downloadArchive(blobUrl, filename)
                    } else {
                      URL.revokeObjectURL(blobUrl)
                    }
                    others.forEach((file) => URL.revokeObjectURL(file.blobUrl))

                    showPreviewGroup(pdfs)
                  })
                  .catch((error) => {
                    console.error(
                      'Nie udalo sie odczytac paczki wydruku:',
                      error,
                    )
                    downloadArchive(blobUrl, filename)
                  })
              } else if (isPdf) {
                showPreview(blobUrl, filename)
              } else {
                const a = document.createElement('a')
                a.href = blobUrl
                a.download = filename
                document.body.appendChild(a)
                a.click()
                document.body.removeChild(a)
                URL.revokeObjectURL(blobUrl)
              }
              showMessage({
                text: t('printout.processed-title'),
                details: t('printout.processed-details').replace(
                  '{0}',
                  t(getReportNameByType(completedTask.data)),
                ),
                type: MessageType.INFO,
                freeze: false,
              } as MessageModel)
            })
            .catch((error) => {
              let code: string | undefined
              // message niesie JSON z listą konkretnych braków (kierowca + okres + czego zabrakło).
              let diagnosis: string | undefined
              try {
                const data = error?.response?.data
                const text =
                  data instanceof ArrayBuffer
                    ? new TextDecoder('utf-8').decode(data)
                    : typeof data === 'string'
                      ? data
                      : JSON.stringify(data)
                const parsed = JSON.parse(text)
                code = parsed?.code
                diagnosis = parsed?.message
              } catch {}
              const details = resolveEmptyDocumentDetails(
                t,
                code,
                completedTask.message,
                diagnosis,
                completedTask.logs,
              )

              if (isBatchDocument) {
                useBatchPrintoutStore.getState().resolveTask(completedTask.id, {
                  status: 'error',
                  errorDetails: details,
                })
                flushFinishedBatch()
                return
              }

              showMessage({
                text: t('printout.processed-error'),
                details,
                type: MessageType.WARNING,
                // Lista braków jest do przeczytania, nie do przeoczenia.
                freeze: true,
              } as MessageModel)
            })
        } else if (isBatchDocument) {
          useBatchPrintoutStore.getState().resolveTask(completedTask.id, {
            status: 'error',
            errorDetails: isEmptyDocumentCode(completedTask.message)
              ? resolveEmptyDocumentDetails(
                  t,
                  completedTask.message,
                  completedTask.message,
                  null,
                  completedTask.logs,
                )
              : completedTask.message || t('errorCode.INSUFFICIENT_DATA'),
          })
          flushFinishedBatch()
        } else {
          showMessage({
            text: t('printout.processed-error'),
            details: isEmptyDocumentCode(completedTask.message)
              ? resolveEmptyDocumentDetails(
                  t,
                  completedTask.message,
                  completedTask.message,
                  null,
                  completedTask.logs,
                )
              : completedTask.message || t('errorCode.INSUFFICIENT_DATA'),
            type: MessageType.WARNING,
            freeze: false,
          } as MessageModel)
        }
      } else if (completedTask.type === TaskType.Violation) {
        showMessage({
          text:
            completedTask.status === TaskStatusEnum.Completed
              ? t('driverViolations.calculated-title')
              : t('driverViolations.calculated-error'),
          details:
            completedTask.status === TaskStatusEnum.Completed
              ? t('driverViolations.calculated-details').replace(
                  '{0}',
                  t(getReportNameByType(completedTask.data)),
                )
              : completedTask.message || '',
          type:
            completedTask.status === TaskStatusEnum.Completed
              ? MessageType.INFO
              : MessageType.ERROR,
          freeze: completedTask.status === TaskStatusEnum.Error,
        } as MessageModel)
      } else if (completedTask.type === TaskType.BusinessTrip) {
        const coDriverDecisionState = getCoDriverDecisionState(completedTask)
        if (coDriverDecisionState) {
          setCoDriverDecision(coDriverDecisionState)
          taskVisited([completedTask.id])
          return
        }

        recordBusinessTripFailures(companyId, completedTask)

        // Wsadowe generowanie i zapis delegacji konczy sie w tle, wiec znaczniki zadan przy
        // kierowcach uniewazniamy dopiero tutaj - w chwili wyslania zadania delegacji jeszcze
        // nie ma, a odswiezenie na tamtym etapie pokazaloby stan sprzed generowania.
        queryClient.invalidateQueries({
          predicate: (query) => query.queryKey[0] === 'driverRequiredActions',
        })

    
        const isBulkOperation =
          (completedTask.data as any)?.operation === 'tripBulkOperation'
        const isCostsRefresh = (completedTask as any).scopeKey
          ? String((completedTask as any).scopeKey).startsWith('btc:')
          : (completedTask.data as any)?.operation === 'refreshCosts'

        showMessage({
          text:
            completedTask.status === TaskStatusEnum.Completed
              ? t(
                  isBulkOperation
                    ? 'businessTrip.bulk-operation-done-title'
                    : isCostsRefresh
                      ? 'businessTrip.costs-refreshed-title'
                      : 'newDelegation.trips-generated-title',
                )
              : t(
                  isBulkOperation || isCostsRefresh
                    ? 'businessTrip.operation-error'
                    : 'businessTrip.generate-failed',
                ),
          details: getBusinessTripTaskDetails(completedTask, t),
          type:
            completedTask.status === TaskStatusEnum.Completed
              ? MessageType.INFO
              : MessageType.ERROR,
          freeze: completedTask.status === TaskStatusEnum.Error,
        } as MessageModel)
      } else if (completedTask.type === TaskType.SaveImport) {
        showMessage({
          text:
            completedTask.status === TaskStatusEnum.Completed
              ? t('importPanel.calculated-title')
              : t('importPanel.calculated-error'),
          details:
            completedTask.status === TaskStatusEnum.Completed
              ? t('importPanel.calculated-details').replace(
                  '{0}',
                  t(getReportNameByType(completedTask.data)),
                )
              : completedTask.message || '',
          type:
            completedTask.status === TaskStatusEnum.Completed
              ? MessageType.INFO
              : MessageType.ERROR,
          freeze: completedTask.status === TaskStatusEnum.Error,
        } as MessageModel)

        // The save wrote drivers/vehicles/activities. Activities are not
        // react-query backed (they read on mount and off this store counter),
        // so both refresh paths are needed for the data to appear without a
        // manual reload.
        invalidateAfterImport(queryClient)
        useDriverActivityStore.getState().triggerRefresh()

        // Deliberately no navigation. This branch was unreachable until the save
        // task started being registered globally, and its old history.push to the
        // import summary would now fire wherever the user happens to be. The
        // import popup holds the user until the save finishes and shows the
        // summary itself, so there is nothing left for a redirect to do.
      } else if (completedTask.type === TaskType.ImiExport) {
        useImiExportErrorsStore.getState().recordResult(
          completedTask.companyId || companyId,
          completedTask.logs,
        )
        showMessage({
          text:
            completedTask.status === TaskStatusEnum.Completed
              ? t('imiExport.calculated-title')
              : t('imiExport.calculated-error'),
          details:
            completedTask.status === TaskStatusEnum.Completed
              ? t('imiExport.calculated-details')
              : completedTask.message || '',
          type:
            completedTask.status === TaskStatusEnum.Completed
              ? MessageType.INFO
              : MessageType.ERROR,
          freeze: completedTask.status === TaskStatusEnum.Error,
        } as MessageModel)

        if (!completedTask.message || completedTask.message === '') {
          // Auto-download the declarations package (ZIP with a folder per driver, or a single
          // PDF when there is exactly one declaration) produced by the IMI export job.
          if (completedTask.status === TaskStatusEnum.Completed) {
            fetchBlobForPreview(
              `/config-api/importTask/downloadOutputFile?id=${completedTask.id}`,
              {},
            )
              .then(({ blobUrl, filename }) => {
                const a = document.createElement('a')
                a.href = blobUrl
                a.download = filename
                document.body.appendChild(a)
                a.click()
                document.body.removeChild(a)
                URL.revokeObjectURL(blobUrl)
              })
              .catch(() => {
                // No output file / download failed — declarations were still created.
              })
          }
        }
      }

      taskVisited([completedTask.id])
    })

    prevCompletedTasksRef.current = completedTasks
  }, [
    completedTasks,
    companyId,
    history,
    showMessage,
    showPreview,
    taskVisited,
    t,
  ])

  const coDriverDecisionModal = coDriverDecision ? (
    <div className="tw-fixed tw-inset-0 tw-z-[10000] tw-flex tw-items-center tw-justify-center tw-bg-black/50">
      <div className="tw-w-[560px] tw-max-w-[calc(100vw-32px)] tw-rounded-lg tw-bg-white tw-shadow-xl">
        <div className="tw-flex tw-items-center tw-gap-3 tw-rounded-t-lg tw-bg-[#FFA928] tw-px-4 tw-py-3">
          <span className="tw-text-base tw-font-medium tw-text-white">
            {t('newDelegation.delegations-for-copying-title')}
          </span>
        </div>
        <div className="tw-p-4">
          <p className="tw-mb-3 tw-text-sm tw-text-gray-700">
            {t('newDelegation.delegations-for-copying-details')}
          </p>
          <div className="tw-mb-4 tw-flex tw-max-h-[360px] tw-flex-col tw-gap-3 tw-overflow-y-auto">
            {coDriverDecision.drivers.map((driver) => (
              <div
                key={driver.driverId}
                className="tw-rounded tw-border tw-border-gray-200 tw-p-3"
              >
                <div className="tw-mb-2 tw-text-xs tw-font-semibold tw-uppercase tw-text-gray-500">
                  Kierowca ID: {driver.driverId}
                </div>
                <div className="tw-flex tw-flex-col tw-gap-2">
                  {driver.options.map((option) => (
                    <label
                      key={option.key}
                      className="tw-flex tw-cursor-pointer tw-items-start tw-gap-2 tw-text-sm tw-text-gray-800"
                    >
                      <input
                        type="checkbox"
                        className="tw-mt-0.5 tw-shrink-0"
                        checked={coDriverDecision.checked[option.key] ?? true}
                        disabled={coDriverDecision.saving}
                        onChange={() => {
                          setCoDriverDecision({
                            ...coDriverDecision,
                            checked: {
                              ...coDriverDecision.checked,
                              [option.key]: !(
                                coDriverDecision.checked[option.key] ?? true
                              ),
                            },
                          })
                        }}
                      />
                      <span>
                        {t(
                          'newDelegation.delegations-for-copying-item-delegation',
                        )}{' '}
                        <strong>{option.tripDates}</strong>{' '}
                        {t(
                          'newDelegation.delegations-for-copying-item-codriver',
                        )}{' '}
                        <strong>{option.coDriverName}</strong>
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="tw-flex tw-justify-end tw-gap-3">
            <button
              className="tw-rounded tw-bg-[#FFA928] tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white hover:tw-bg-[#e09520] disabled:tw-opacity-60"
              disabled={coDriverDecision.saving}
              onClick={() => handleCoDriverDecisionSave(false)}
            >
              {t('newDelegation.delegations-for-copying-button-skip')}
            </button>
            <button
              className="tw-rounded tw-bg-[#2448bb] tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white hover:tw-bg-[#1a3a9e] disabled:tw-opacity-60"
              disabled={coDriverDecision.saving}
              onClick={() => handleCoDriverDecisionSave(true)}
            >
              {t('newDelegation.delegations-for-copying-button-copy')}
            </button>
          </div>
        </div>
      </div>
    </div>
  ) : null


  useElapsedTick(tasks.length > 0)

  if (tasks.length === 0) {
    return coDriverDecisionModal
  }

  const isViolationCalculationInProgress = tasks.some(
    (task) => task.type === TaskType.Violation,
  )
  const isTimesheetInProgress = tasks.some(
    (task) => task.type === TaskType.Timesheet,
  )
  const isSaveImportInProgress = tasks.some(
    (task) => task.type === TaskType.SaveImport,
  )
  const imiExportInProgress = tasks.some(
    (task) => task.type === TaskType.ImiExport,
  )
  const businessTripGenerationInProgress = tasks.some(
    (task) => task.type === TaskType.BusinessTrip,
  )

  const businessTripCostsRefreshInProgress = tasks.some(
    (task) =>
      task.type === TaskType.BusinessTrip &&
      ((task as any).scopeKey
        ? String((task as any).scopeKey).startsWith('btc:')
        : (task as any).data?.operation === 'refreshCosts'),
  )
  const businessTripBulkOperationInProgress = tasks.some(
    (task) =>
      task.type === TaskType.BusinessTrip &&
      (task as any).data?.operation === 'tripBulkOperation',
  )

  const progressTask = tasks.find((task) => task.progressTotal) as any
  const progressView = describeTaskProgress(progressTask, t)


  const percent =
    progressView.phase === 'composing' && progressTask?.progressTotal
      ? ((progressTask.progressCurrent ?? 0) / progressTask.progressTotal) * 100
      : null


  const statusLabel = isTimesheetInProgress
    ? t('timesheet.calculation-in-progress')
    : isViolationCalculationInProgress
      ? t('driverViolations.calculation-in-progress')
      : isSaveImportInProgress
        ? t('importPanel.calculation-in-progress')
        : imiExportInProgress
          ? t('exportModule.submitting-in-progress')
          : businessTripBulkOperationInProgress
            ? t('businessTrip.bulk-operation-in-progress')
            : businessTripCostsRefreshInProgress
              ? t('businessTrip.costs-refresh-in-progress')
              : businessTripGenerationInProgress
                ? t('businessTrip.generation-in-progress')
                : t('printout.processing-in-progress')

  
  return (
    <>
      <TaskStatusBanner
        label={statusLabel}
        detail={progressView.suffix.trim()}
        percent={percent}
      />
      {coDriverDecisionModal}
    </>
  )
}

const Routed = withRouter(TaskStatusComponent) as any

const TaskStatusWithActions: React.FC<any> = (props) => (
  <Routed {...props} showMessage={showMessage} addTask={addTask} />
)

export const TaskStatus = withSelectedCompany(TaskStatusWithActions)
